/**
 * INTERACTIVE STOCK RESEARCH — a session with the stock analyst agent.
 *
 *   useStockAnalystSessions       the reader's sessions on a stock, to resume.
 *   useStockAnalystSession        one session — see analystSession.js for
 *                                 the approvals, PDF and closing.
 *
 * A session is opened, and the analyst talked to, in the assistant's own
 * chat: `useCopilot().openStockResearch(symbol)` mints the web token
 * (createLivekitTokenStockResearchInstitutionalWeb), which opens or re-opens
 * the reader's session on the stock and names it as `analystSessionId`.
 */
import { useQuery } from '@apollo/client';
import {
  STOCK_ANALYST_PENDING_ACTIONS, STOCK_ANALYST_REVISION, STOCK_ANALYST_REVISIONS, STOCK_ANALYST_SESSION, STOCK_ANALYST_SESSIONS,
} from '../queries/stockAnalyst';
import {
  CLOSE_STOCK_ANALYST_SESSION, CONFIRM_STOCK_ANALYST_ACTION, DOWNLOAD_STOCK_ANALYST_REPORT, ISSUE_STOCK_ANALYST_TOKEN,
  REJECT_STOCK_ANALYST_ACTION,
} from '../mutations/stockAnalyst';
import { STOCK_RESEARCH_CHAT } from '../subscriptions/stockAnalyst';
import { EMPTY } from './_shared';
import { useAnalystSession } from './analystSession';

export {
  AUTO_CLOSE_MS, chatEventKind, isClosedStatus, parseJson,
} from './analystSession';

/** The stock analyst's backend, as useAnalystSession reads it. */
const STOCK_API = {
  key: 'stockAnalyst',
  docs: {
    session: STOCK_ANALYST_SESSION,
    revisions: STOCK_ANALYST_REVISIONS,
    revision: STOCK_ANALYST_REVISION,
    pending: STOCK_ANALYST_PENDING_ACTIONS,
    chat: STOCK_RESEARCH_CHAT,
    confirm: CONFIRM_STOCK_ANALYST_ACTION,
    reject: REJECT_STOCK_ANALYST_ACTION,
    token: ISSUE_STOCK_ANALYST_TOKEN,
    close: CLOSE_STOCK_ANALYST_SESSION,
    download: DOWNLOAD_STOCK_ANALYST_REPORT,
  },
  field: {
    session: 'stockAnalystSession',
    revisions: 'stockAnalystRevisions',
    revision: 'stockAnalystRevision',
    pending: 'stockAnalystPendingActions',
    chat: 'stockResearchChat',
    confirm: 'confirmStockAnalystAction',
    reject: 'rejectStockAnalystAction',
    token: 'issueStockAnalystConfirmationToken',
    close: 'closeStockAnalystSession',
    download: 'downloadStockAnalystReport',
  },
  chatVars: { surface: 'app' },
  sessionsQuery: 'StockAnalystSessions',
};

/** The reader's fundamental-research sessions on one stock, newest first. */
export function useStockAnalystSessions(securitySymbol, { limit = 10, skip = false } = {}) {
  const q = useQuery(STOCK_ANALYST_SESSIONS, {
    variables: { securitySymbol, analystKind: 'fundamental', limit },
    skip: skip || !securitySymbol,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return {
    sessions: q.data?.stockAnalystSessions ?? EMPTY,
    loading: q.loading,
    error: q.error ?? null,
    refetch: q.refetch,
  };
}

/** One stock research session. Mount it keyed by the session id. */
export const useStockAnalystSession = (sessionId) => useAnalystSession(sessionId, STOCK_API);
